import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import SEOMetaTags from '@/components/SEOMetaTags';
import SEOStructuredData from '@/components/SEOStructuredData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CalendarIcon, ClockIcon } from 'lucide-react';

interface Article {
  id: string;
  title: string;
  content: string;
  date: string;
  topic: string;
  slug: string;
  image_url: string | null;
  category: {
    name: string;
  } | null;
}

export default function Articles() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select(`
          id,
          title,
          content,
          date,
          topic,
          slug,
          image_url,
          category:category_id(name)
        `)
        .eq('publication_state', 'published')
        .order('date', { ascending: false });

      if (error) throw error;
      setArticles(data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateReadingTime = (content: string) => {
    const textContent = content.replace(/<[^>]*>/g, ' ');
    const wordCount = textContent.trim().split(/\s+/).length;
    return Math.max(1, Math.ceil(wordCount / 200));
  };

  const truncateDescription = (content: string, maxLength: number = 150) => {
    const textContent = content.replace(/<[^>]*>/g, ' ');
    return textContent.length > maxLength 
      ? textContent.substring(0, maxLength) + '...'
      : textContent;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#0a0a0a]">
        <Header />
        <LoadingSpinner message="Loading articles..." />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#0a0a0a]">
      <SEOMetaTags
        title="All Articles - AI Insights & Tech Tutorials by Aram AI"
        description="Explore all articles by Aram AI covering AI tools, entrepreneurship, Armenian tech community, productivity tips, and AI solopreneur journey insights."
        keywords="AI articles, tech tutorials, entrepreneurship, Armenian AI, artificial intelligence, productivity, tech blog"
        canonicalUrl="https://tezzarida.io/articles"
        ogType="website"
      />
      <SEOStructuredData
        type="website"
        title="All Articles - AI Insights & Tech Tutorials"
        description="Comprehensive collection of AI insights, tech tutorials, and entrepreneurship articles by Aram AI."
        url="https://tezzarida.io/articles"
      />
      
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-12 max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-heading glow-text">
            All Articles
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Explore insights on AI, entrepreneurship, and the Armenian tech community
          </p>
        </div>

        {articles.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No articles found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article) => (
              <Link key={article.id} to={`/article/${article.slug}`}>
                <Card className="glass-panel hover:border-purple-500/50 transition-all duration-300 h-full">
                  {article.image_url && (
                    <div className="aspect-video overflow-hidden rounded-t-lg">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      {article.category && (
                        <Badge variant="secondary" className="text-xs">
                          {article.category.name}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {article.topic}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg font-semibold text-white line-clamp-2">
                      {article.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-300 mb-4 line-clamp-3">
                      {truncateDescription(article.content)}
                    </CardDescription>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        {new Date(article.date).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1">
                        <ClockIcon className="w-4 h-4" />
                        {calculateReadingTime(article.content)} min read
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
}